import { PropsWithChildren, createContext, useReducer } from 'react';
import dayjs from 'dayjs';

type MedicinesState = { startDate: string; endDate: string; day: string };

type HandleMonthAndWeekPayload = 'prev' | 'next';

type MedicinesAction =
  | { type: 'SET_SELECTED'; payload: Partial<MedicinesState> }
  | { type: 'HANDLE_MONTH'; payload: HandleMonthAndWeekPayload }
  | { type: 'HANDLE_WEEK'; payload: HandleMonthAndWeekPayload };

export const MedicinesContext = createContext<
  | {
      state: MedicinesState;
      setSelected: (payload: Partial<MedicinesState>) => void;
      handleMonth: (payload: HandleMonthAndWeekPayload) => void;
      handleWeek: (payload: HandleMonthAndWeekPayload) => void;
    }
  | undefined
>(undefined);

const reducer = (state: MedicinesState, action: MedicinesAction): MedicinesState => {
  switch (action.type) {
    case 'SET_SELECTED':
      return { ...state, ...action.payload };
    case 'HANDLE_MONTH':
      // NOTE: https://github.com/facebook/react/issues/16295#issuecomment-1509218639
      const newHandleMonthState = { ...state };

      if (action.payload === 'prev') {
        const firstDayOfPrevMonth = dayjs(state.startDate).subtract(1, 'month').startOf('month');

        newHandleMonthState.day = firstDayOfPrevMonth.format();
        newHandleMonthState.startDate = firstDayOfPrevMonth.format();
        newHandleMonthState.endDate = firstDayOfPrevMonth.add(6, 'day').format();
      } else {
        const firstDayOfNextMonth = dayjs(state.startDate).add(1, 'month').startOf('month');

        newHandleMonthState.day = firstDayOfNextMonth.format();
        newHandleMonthState.startDate = firstDayOfNextMonth.format();
        newHandleMonthState.endDate = firstDayOfNextMonth.add(6, 'day').format();
      }

      return newHandleMonthState;
    case 'HANDLE_WEEK':
      // NOTE: https://github.com/facebook/react/issues/16295#issuecomment-1509218639
      const newHandleWeekState = { ...state };

      if (action.payload === 'prev') {
        const firstDayOfPrevWeek = dayjs(state.startDate).subtract(1, 'week');

        newHandleWeekState.day = firstDayOfPrevWeek.format();
        newHandleWeekState.startDate = firstDayOfPrevWeek.format();
        newHandleWeekState.endDate = firstDayOfPrevWeek.add(6, 'day').format();
      } else {
        const firstDayOfNextWeek = dayjs(state.startDate).add(1, 'week');

        newHandleWeekState.day = firstDayOfNextWeek.format();
        newHandleWeekState.startDate = firstDayOfNextWeek.format();
        newHandleWeekState.endDate = firstDayOfNextWeek.add(6, 'day').format();
      }

      return newHandleWeekState;
    default:
      return state;
  }
};

const initialState: MedicinesState = {
  startDate: dayjs().format(),
  endDate: dayjs().add(6, 'day').format(),
  day: dayjs().format(),
};

export const MedicinesProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setSelected = (payload: Partial<MedicinesState>) => {
    dispatch({ type: 'SET_SELECTED', payload });
  };

  const handleMonth = (payload: HandleMonthAndWeekPayload) => {
    dispatch({ type: 'HANDLE_MONTH', payload });
  };

  const handleWeek = (payload: HandleMonthAndWeekPayload) => {
    dispatch({ type: 'HANDLE_WEEK', payload });
  };

  return (
    <MedicinesContext.Provider value={{ state, setSelected, handleMonth, handleWeek }}>
      {children}
    </MedicinesContext.Provider>
  );
};
